import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Typography,
  FormHelperText,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { enqueueSnackbar } from 'notistack';

import templateService, { Template, TemplateCreateData, TemplateUpdateData } from '../../services/templateService';
import SysEditor from '../syseditor/SysEditor';

// Schema de validação
const templateSchema = yup.object().shape({
  title: yup
    .string()
    .required('Título é obrigatório')
    .min(1, 'Título deve ter pelo menos 1 caractere')
    .max(255, 'Título deve ter no máximo 255 caracteres'),
  content: yup
    .string()
    .required('Conteúdo é obrigatório')
    .min(1, 'Conteúdo não pode estar vazio'),
  type: yup
    .string()
    .required('Tipo é obrigatório')
    .oneOf(['email', 'sms', 'termos', 'recibo'], 'Tipo inválido'),
  isActive: yup
    .boolean()
    .default(true),
}) as yup.ObjectSchema<FormData>;

interface TemplateFormProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  template?: Template | null;
}

interface FormData {
  title: string;
  content: string;
  type: 'email' | 'sms' | 'termos' | 'recibo';
  isActive: boolean;
}

const TemplateForm: React.FC<TemplateFormProps> = ({
  open,
  onClose,
  onSuccess,
  template
}) => {
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm<FormData>({
    resolver: yupResolver(templateSchema) as any,
    defaultValues: {
      title: '',
      content: '',
      type: 'email',
      isActive: true,
    }
  });

  const watchedType = watch('type');

  // Preencher formulário quando template é passado para edição
  useEffect(() => {
    if (template) {
      reset({
        title: template.title,
        content: template.content,
        type: template.type as 'email' | 'sms' | 'termos' | 'recibo',
        isActive: template.isActive,
      });
    } else {
      reset({
        title: '',
        content: '',
        type: 'email',
        isActive: true,
      });
    }
  }, [template, reset]);

  const onSubmit = async (data: FormData) => {
    setLoading(true);
    try {
      if (template) {
        // Editar template existente
        const updateData: TemplateUpdateData = {
          title: data.title,
          content: data.content,
          type: data.type,
          isActive: data.isActive,
        };
        await templateService.updateTemplate(template.id, updateData);
        enqueueSnackbar('Template atualizado com sucesso!', { variant: 'success' });
      } else {
        // Criar novo template
        const createData: TemplateCreateData = {
          title: data.title,
          content: data.content,
          type: data.type,
          isActive: data.isActive,
        };
        await templateService.createTemplate(createData);
        enqueueSnackbar('Template criado com sucesso!', { variant: 'success' });
      }
      
      onSuccess();
      handleClose();
    } catch (error: any) {
      console.error('Erro ao salvar template:', error);
      const message = error.response?.data?.error || 'Erro ao salvar template';
      enqueueSnackbar(message, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      reset();
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: '90vh' }
      }}
    >
      <DialogTitle sx={{ p: 1.5 }}>
        <Typography variant="h6" sx={{ fontSize: '1.1rem' }}>
          {template ? 'Editar Template' : 'Novo Template'}
        </Typography>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        <Box component="form" noValidate>
          <Grid container spacing={1.5}>
            {/* Linha 1: Título e Tipo */}
            <Grid item xs={12} md={8}>
              <Controller
                name="title"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Título"
                    error={!!errors.title}
                    helperText={errors.title?.message}
                    disabled={loading}
                  />
                )}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <Controller
                name="type"
                control={control}
                render={({ field }) => (
                  <FormControl fullWidth error={!!errors.type}>
                    <InputLabel>Tipo</InputLabel>
                    <Select
                      {...field}
                      label="Tipo"
                      disabled={loading}
                    >                      
                      <MenuItem value="email">E-mail</MenuItem>
                      <MenuItem value="sms">SMS</MenuItem>
                      <MenuItem value="termos">Termos</MenuItem>
                      <MenuItem value="recibo">Recibo</MenuItem>
                    </Select>
                    {errors.type && (
                      <FormHelperText>{errors.type.message}</FormHelperText>
                    )}
                  </FormControl>
                )}
              />
            </Grid>

            {/* Editor de Conteúdo */}
            <Grid item xs={12}>
              <Controller
                name="content"
                control={control}
                render={({ field }) => (
                  <SysEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Digite o conteúdo do template aqui..."
                    disabled={loading}
                    height={500}
                    showVariables={true}
                    variablesType={watchedType}
                  />
                )}
              />
            </Grid>

            {/* Status Ativo */}
            <Grid item xs={12}>
              <Controller
                name="isActive"
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    control={
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={loading}
                      />
                    }
                    label="Template Ativo"
                  />
                )}
              />
            </Grid>
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 1.5 }}>
        <Button onClick={handleClose} disabled={loading} size="small" sx={{ fontSize: '0.8rem' }}>
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant="contained"
          disabled={loading}
          size="small"
          sx={{ fontSize: '0.8rem' }}
        >
          {loading ? 'Salvando...' : (template ? 'Atualizar' : 'Criar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TemplateForm; 